import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Archive, ExternalLink, FileText, Loader2, RefreshCw, Search } from "lucide-react";
import { Link } from "react-router-dom";

const STATI = { completato: "Completata", in_elaborazione: "In elaborazione", da_analizzare: "Da analizzare", errore: "Errore" };
const FONTI = { rete: "Rete", self_service: "Configuratore self-service", area_free: "Area Free", crm: "CRM (commerciale)" };
const STATO_CLS = { completato: "bg-emerald-100 text-emerald-800", in_elaborazione: "bg-amber-100 text-amber-800", errore: "bg-red-100 text-red-800", da_analizzare: "bg-slate-200 text-slate-700" };
const fmt = (v, d = 0) => (v == null ? "—" : Number(v).toLocaleString("it-IT", { maximumFractionDigits: d }));

const Kpi = ({ label, value, testid }) => (
  <Card className="rounded border-border p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{label}</div>
    <div className="text-2xl font-heading font-bold mt-1">{value}</div>
  </Card>
);

const Copie = ({ b, onOpen }) => b.copie.map((c) => (
  <tr key={c.id} className="bg-secondary/30 text-muted-foreground" data-testid={`bolletta-copia-${c.id}`}>
    <td className="px-3 py-1.5 pl-6 text-[11px] whitespace-nowrap">↳ {new Date(c.created_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</td>
    <td className="px-3 py-1.5 text-[10px]">copia</td>
    <td className="px-3 py-1.5 text-[11px] whitespace-nowrap">{c.fonte_label}</td>
    <td className="px-3 py-1.5 text-[11px]" colSpan={6}>{c.intestatario || "—"} · {c.filename || ""}</td>
    <td />
    <td className="px-3 py-1.5 text-right whitespace-nowrap">
      <Button size="sm" variant="ghost" onClick={() => onOpen(c)} data-testid={`bolletta-copia-open-${c.id}`}><FileText className="h-3.5 w-3.5" /></Button>
      {c.lead_eliminato ? <span className="text-[10px] mr-1">lead eliminato</span> : c.lead_id && <Button asChild size="sm" variant="ghost"><Link to={`/leads/${c.lead_id}`} data-testid={`bolletta-copia-lead-${c.id}`}><ExternalLink className="h-3.5 w-3.5" /></Link></Button>}
    </td>
  </tr>
));

const Row = ({ b, onOpen, onRetry }) => {
  const [open, setOpen] = useState(false);
  return (<>
  <tr className="border-t border-border hover:bg-secondary/40" data-testid={`bolletta-row-${b.id}`}>
    <td className="px-3 py-2 text-xs whitespace-nowrap">{new Date(b.created_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}
      {b.n_copie > 1 && <button type="button" onClick={() => setOpen(!open)} className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-primary/15 text-primary hover:bg-primary/25" data-testid={`bolletta-copie-toggle-${b.id}`}>×{b.n_copie} copie</button>}
    </td>
    <td className="px-3 py-2"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded whitespace-nowrap ${STATO_CLS[b.stato] || ""}`} title={b.errore || ""} data-testid={`bolletta-stato-${b.id}`}>{STATI[b.stato] || b.stato}</span>
      {b.da_verificare && <span className="ml-1 text-[10px] font-semibold px-2 py-0.5 rounded whitespace-nowrap bg-orange-100 text-orange-800" title="Consumo annuo stimato: storico della bolletta incoerente o assente" data-testid={`bolletta-verificare-${b.id}`}>Da verificare</span>}</td>
    <td className="px-3 py-2 text-xs whitespace-nowrap" data-testid={`bolletta-fonte-${b.id}`}>{b.fonte_label || FONTI[b.fonte]}</td>
    <td className="px-3 py-2 text-xs"><div className="font-medium">{b.intestatario || "—"}</div><div className="text-muted-foreground">{b.indirizzo || ""}</div></td>
    <td className="px-3 py-2 text-xs capitalize">{b.tipo || "—"}</td>
    <td className="px-3 py-2 text-xs font-mono">{b.pod || "—"}</td>
    <td className="px-3 py-2 text-xs">{b.fornitore || "—"}</td>
    <td className="px-3 py-2 text-xs text-right">{fmt(b.consumo_annuo_kwh)}{b.consumo_annuo_stimato && <span className="text-orange-700" title="stimato"> *</span>}</td>
    <td className="px-3 py-2 text-xs text-right">{b.prezzo_energia_eur_kwh ? fmt(b.prezzo_energia_eur_kwh, 3) : "—"}</td>
    <td className="px-3 py-2 text-xs text-right">{fmt(b.potenza_contatore_kw, 1)}</td>
    <td className="px-3 py-2 text-right whitespace-nowrap">
      <Button size="sm" variant="ghost" onClick={() => onOpen(b)} data-testid={`bolletta-open-${b.id}`}><FileText className="h-4 w-4" /></Button>
      {b.lead_eliminato && <span className="text-[10px] text-muted-foreground mr-1" data-testid={`bolletta-lead-eliminato-${b.id}`}>lead eliminato</span>}
      {b.lead_id && !b.lead_eliminato && <Button asChild size="sm" variant="ghost"><Link to={`/leads/${b.lead_id}`} data-testid={`bolletta-lead-${b.id}`} title="Apri lead"><ExternalLink className="h-4 w-4" /></Link></Button>}
      {["errore", "da_analizzare"].includes(b.stato) && <Button size="sm" variant="ghost" onClick={() => onRetry(b)} data-testid={`bolletta-retry-${b.id}`}><RefreshCw className="h-4 w-4" /></Button>}
    </td>
  </tr>
  {open && <Copie b={b} onOpen={onOpen} />}
  </>);
};

export default function ArchivioBollette() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [stato, setStato] = useState("");
  const [fonte, setFonte] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/bollette", { params: { q, stato, fonte } })
    .then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))), [q, stato, fonte]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/bollette/analizza-pendenti");
      await load();
      toast.success(r.data.in_coda ? `Archivio aggiornato · ${r.data.in_coda} bollette in analisi` : "Archivio aggiornato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const open = async (b) => {
    const w = window.open("", "_blank");
    try {
      const r = await api.get(`/admin/bollette/${b.id}/file`, { responseType: "blob" });
      w.location.href = URL.createObjectURL(r.data);
    } catch (e) { w.close(); toast.error("Impossibile aprire il file"); }
  };
  const retry = async (b) => {
    try { await api.post(`/admin/bollette/${b.id}/rianalizza`); toast.success("Analisi riavviata"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="archivio-bollette-page">
      <header className="sticky top-14 lg:top-0 z-10 h-16 border-b border-border bg-background flex items-center justify-between px-4 lg:px-8">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary/10 p-2"><Archive className="h-5 w-5 text-primary" strokeWidth={1.5} /></div>
          <div><h1 className="text-xl font-heading font-bold">Archivio bollette</h1><p className="text-xs text-muted-foreground">Tutte le bollette acquisite (Rete, self-service, Area Free, CRM) · benchmark interno</p></div>
        </div>
        <Button size="sm" variant="outline" onClick={refresh} disabled={busy} data-testid="bollette-refresh-btn"><RefreshCw className={`h-4 w-4 mr-1.5 ${busy ? "animate-spin" : ""}`} /> {busy ? "Aggiorno…" : "Aggiorna"}</Button>
      </header>
      <div className="p-4 lg:p-8 space-y-6">
        {!data ? <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div> : (<>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Kpi label={data.copie_escluse ? `Bollette uniche · ${data.copie_escluse} copie escluse` : "Bollette uniche"} value={data.totale} testid="bollette-kpi-totale" />
            <Kpi label="Analizzate" value={data.completate} testid="bollette-kpi-completate" />
            <Kpi label="Mediana prezzo energia" value={data.mediana_prezzo_eur_kwh ? `${fmt(data.mediana_prezzo_eur_kwh, 3)} €/kWh` : "—"} testid="bollette-kpi-mediana" />
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per intestatario, POD, fornitore…" className="pl-8 h-9" data-testid="bollette-search-input" />
            </div>
            <select value={fonte} onChange={(e) => setFonte(e.target.value)} className="h-9 rounded-md border border-input bg-transparent px-2 text-sm" data-testid="bollette-fonte-select">
              <option value="">Tutte le fonti</option>
              {Object.entries(FONTI).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            {["", "completato", "da_analizzare", "in_elaborazione", "errore"].map((s) => (
              <Badge key={s || "all"} variant={stato === s ? "default" : "outline"} className="cursor-pointer" onClick={() => setStato(s)} data-testid={`bollette-filter-${s || "tutte"}`}>{s ? STATI[s] : "Tutte"}</Badge>
            ))}
          </div>
          <Card className="rounded border-border overflow-x-auto">
            <table className="w-full text-left" data-testid="bollette-table">
              <thead className="text-[10px] uppercase tracking-wide text-muted-foreground bg-secondary/50">
                <tr><th className="px-3 py-2">Ricevuta</th><th className="px-3 py-2">Stato</th><th className="px-3 py-2">Fonte</th><th className="px-3 py-2">Intestatario</th><th className="px-3 py-2">Tipo</th><th className="px-3 py-2">POD</th><th className="px-3 py-2">Fornitore</th><th className="px-3 py-2 text-right">kWh/anno</th><th className="px-3 py-2 text-right">€/kWh</th><th className="px-3 py-2 text-right">kW</th><th className="px-3 py-2" /></tr>
              </thead>
              <tbody>
                {data.bollette.map((b) => <Row key={b.id} b={b} onOpen={open} onRetry={retry} />)}
                {!data.bollette.length && <tr><td colSpan={11} className="px-3 py-10 text-center text-sm text-muted-foreground" data-testid="bollette-empty">Nessuna bolletta</td></tr>}
              </tbody>
            </table>
          </Card>
        </>)}
      </div>
    </div>
  );
}
